import type {
  AdminOrderListResponse,
  AdminOrderResponse,
  AdminOrderTodoCounts,
} from '@inflip/contracts/admin';
import { ORDER_STATUS_LABELS, OrderStatus } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { AnimatedCheckbox } from '../components/animated-checkbox';
import { DataTable } from '../components/data-table';
import { DateRangeFilter } from '../components/date-range-filter';
import {
  OrderConfirmPaymentDialog,
  paymentNoLongerPendingMessage,
} from '../components/order-confirm-payment-dialog';
import { TerminationBadge } from '../components/termination-badge';
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { daysAgo, todayDateInput } from '../lib/date-input';
import { ORDER_STATUS_TEXT, orderStatusLabel } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { showToast } from '../lib/toast';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

const PAGE_SIZE = 20;

const COLUMNS: ColumnDef<AdminOrderResponse>[] = [
  {
    id: 'status',
    header: '상태',
    size: 120,
    meta: { align: 'center' },
    cell: ({ row }) => (
      <span className="inline-flex flex-col items-center gap-0.5">
        <span className={`text-xs font-bold ${ORDER_STATUS_TEXT[row.original.status]}`}>
          {orderStatusLabel(row.original.status, row.original.cancelledBy)}
        </span>
        {row.original.status === 'TERMINATED' && row.original.termination && (
          <TerminationBadge termination={row.original.termination} />
        )}
        {/* 추가금은 주문 상태를 바꾸지 않는다 — 입금 대기를 상태 아래에 따로 보인다 */}
        {row.original.addonPayments.some((p) => p.status === 'PENDING') && (
          <span className="text-[11px] font-bold text-amber-600">추가금 입금 대기</span>
        )}
      </span>
    ),
  },
  {
    id: 'order',
    header: '주문',
    cell: ({ row }) => {
      const { id, orderGroupId, groupSize = 1 } = row.original;
      return (
        <span className="flex flex-col gap-0.5">
          <Link
            to="/orders/$orderId"
            params={{ orderId: id }}
            className="font-mono text-xs font-medium text-brand hover:underline"
          >
            {id.slice(0, 8)}
          </Link>
          {/* 입금 확인 뒤 결제 설정의 기준 일수가 지나도록 발급 전 */}
          {row.original.licenseIssueDelayedDays !== null && (
            <span className="text-[11px] font-bold text-rose-600">
              발급 지연 · 입금 확인 {row.original.licenseIssueDelayedDays}일째
            </span>
          )}
          {/* 묶음 주문 — 누르면 기간 없이 그 묶음의 주문만 모아 본다 */}
          {orderGroupId && groupSize > 1 && (
            <Link
              to="/orders"
              search={{ q: orderGroupId, from: '', to: '' }}
              className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
            >
              묶음 <span className="font-mono">{orderGroupId.slice(0, 8)}</span> · {groupSize}건
            </Link>
          )}
        </span>
      );
    },
  },
  {
    id: 'listing',
    header: '리스팅',
    cell: ({ row }) => (
      <span className="block truncate">{row.original.listingTitle ?? '(삭제됨)'}</span>
    ),
  },
  {
    id: 'parties',
    header: '판매자 → 구매자',
    cell: ({ row }) => (
      <span className="block truncate text-xs text-muted-foreground">
        {row.original.sellerName ?? '—'} → {row.original.buyerName ?? '—'}
      </span>
    ),
  },
  {
    id: 'depositReference',
    header: '입금자명',
    cell: ({ row }) => row.original.payment?.depositReference ?? '—',
    meta: { tdClass: 'font-mono text-xs' },
  },
  {
    id: 'amount',
    header: '금액',
    cell: ({ row }) => (
      <span className="flex flex-col">
        <span>
          {row.original.totalAmount.toLocaleString('ko-KR')} {row.original.currency}
        </span>
        {row.original.addonAmount > 0 && (
          <span className="text-[11px] text-muted-foreground">
            추가금 {row.original.addonAmount.toLocaleString('ko-KR')} 포함
          </span>
        )}
      </span>
    ),
    meta: { tdClass: 'whitespace-nowrap tabular-nums' },
  },
  {
    id: 'createdAt',
    header: '생성',
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
];

/** 칩 옆 숫자 — 처리할 일이 쌓이는 상태만 (입금 대기·정산 대기·분쟁 처리 중·추가금 입금 대기·판매자 위반 신고) */
function todoCountOf(
  counts: AdminOrderTodoCounts | null,
  s: OrderStatus | 'ALL' | 'ADDON_PENDING' | 'VIOLATION_REPORTED',
): number {
  if (!counts || s === 'ALL') return 0;
  return (counts as Partial<Record<string, number>>)[s] ?? 0;
}

export function OrdersPage() {
  const { session } = useAdminAuth();
  // 목록 상태는 URL 에 — 상세에서 뒤로 나와도 보던 페이지·필터가 유지된다
  // ADDON_PENDING 은 주문 상태가 아니라 "입금 대기 중인 추가금이 있는 주문" 필터,
  // VIOLATION_REPORTED 는 "처리 중인 판매자 위반 신고가 있는 주문" 필터
  const [filter, setFilter] = useListViewState<
    OrderStatus | 'ALL' | 'ADDON_PENDING' | 'VIOLATION_REPORTED'
  >('status', 'ALL');
  // 결제 확인됨 탭에서만 — 입금 확인 뒤 기준 일수가 지나도록 발급 전인 주문만
  const [delayedOnly, setDelayedOnly] = useListViewState('delayed', false);
  const issueDelayed = filter === OrderStatus.PAID && delayedOnly;
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  // 묶음 번호 링크처럼 주소로 검색어가 바뀌면 입력창도 따라간다
  useEffect(() => setQ(searchTerm), [searchTerm]);
  // 일반 어드민 관례 — 기본은 최근 7일
  const [dateFrom, setDateFrom] = useListViewState('from', daysAgo(7));
  const [dateTo, setDateTo] = useListViewState('to', todayDateInput());
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminOrderListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // 처리 후 목록 다시 불러오기
  const [reloadKey, setReloadKey] = useState(0);
  // 목록에서 바로 입금 확인 — 묶음 주문은 함께 확인되는 주문을 보여 주려고 상세를 불러와 창을 연다
  const [opening, setOpening] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AdminOrderResponse | null>(null);

  const columns = useMemo<ColumnDef<AdminOrderResponse>[]>(
    () => [
      ...COLUMNS,
      {
        id: 'actions',
        header: '처리',
        meta: { align: 'center' },
        cell: ({ row }) => {
          const o = row.original;
          // 추가금 확인은 상세의 처리 영역에서 — 입금자명·금액을 함께 맞춰 본다
          if (o.addonPayments.some((p) => p.status === 'PENDING'))
            return (
              <Link
                to="/orders/$orderId"
                params={{ orderId: o.id }}
                className="text-xs font-bold text-brand hover:underline"
              >
                추가금 확인
              </Link>
            );
          if (o.status !== OrderStatus.PAYMENT_PENDING || o.payment?.status !== 'PENDING')
            return null;
          return (
            <Button
              size="sm"
              // 처리 뒤 목록을 다시 불러오는 동안에는 예전 상태로 다시 열지 않게 막는다
              disabled={opening !== null || loading}
              onClick={() => {
                if (!session) return;
                setOpening(o.id);
                setError(null);
                api
                  .getOrder(session.accessToken, o.id)
                  .then((detail) => {
                    // 목록을 본 뒤 구매자가 취소했거나 다른 어드민이 처리했으면 창 대신 알리고 목록을 갱신
                    const stale = paymentNoLongerPendingMessage(detail);
                    if (stale) {
                      showToast(stale);
                      notifyPendingWorkChanged();
                      setReloadKey((k) => k + 1);
                      return;
                    }
                    setConfirming(detail);
                  })
                  .catch((err) => setError(err instanceof Error ? err.message : String(err)))
                  .finally(() => setOpening(null));
              }}
            >
              {opening === o.id ? '여는 중…' : '입금 확인'}
            </Button>
          );
        },
      },
    ],
    [opening, loading, session],
  );

  // 상태 칩 옆 숫자 — 목록을 다시 불러올 때 같이 (처리하면 줄어든다)
  const [todoCounts, setTodoCounts] = useState<AdminOrderTodoCounts | null>(null);
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .orderTodoCounts(session.accessToken)
      .then((r) => !cancelled && setTodoCounts(r))
      .catch(() => {}); // 숫자는 부가 정보 — 실패해도 목록은 그대로
    return () => {
      cancelled = true;
    };
  }, [session, filter, page, reloadKey]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    api
      .listOrders(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter === 'ALL'
          ? {}
          : filter === 'ADDON_PENDING'
            ? { pendingAddon: true }
            : filter === 'VIOLATION_REPORTED'
              ? { openViolationReport: true }
              : { status: filter }),
        ...(issueDelayed ? { issueDelayed: true } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      })
      .then((r) => !cancelled && setResult(r))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, filter, issueDelayed, page, searchTerm, dateFrom, dateTo, reloadKey]);

  if (!session) return null;

  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="주문 관리"
        description={result ? `전체 ${result.total.toLocaleString('ko-KR')}건` : undefined}
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q);
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="주문·묶음 번호 / 입금자명 / 구매자·판매자 / 상품"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {(
          ['ALL', ...Object.values(OrderStatus), 'ADDON_PENDING', 'VIOLATION_REPORTED'] as const
        ).map((s) => (
          <Chip
            key={s}
            active={filter === s}
            onClick={() => {
              setPage(1);
              setFilter(s);
            }}
          >
            {s === 'ALL'
              ? '전체'
              : s === 'ADDON_PENDING'
                ? '추가금 입금 대기'
                : s === 'VIOLATION_REPORTED'
                  ? '판매자 위반 신고'
                  : ORDER_STATUS_LABELS[s]}
            {todoCountOf(todoCounts, s) > 0 && (
              <span
                className={`ml-1 tabular-nums ${filter === s ? 'text-background/70' : 'font-bold text-rose-600'}`}
              >
                {todoCountOf(todoCounts, s).toLocaleString('ko-KR')}
              </span>
            )}
          </Chip>
        ))}
        {filter === OrderStatus.PAID && (
          <label className="ml-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <AnimatedCheckbox
              checked={delayedOnly}
              onChange={(e) => {
                setPage(1);
                setDelayedOnly(e.target.checked);
              }}
            />
            발급 지연만
          </label>
        )}
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !result && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message="조건에 맞는 주문이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(o) => o.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages || loading}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {/* 그사이 상태가 바뀌어 더는 입금 대기가 아니면 창을 열지 않는다 */}
      {confirming?.payment?.status === 'PENDING' && (
        <OrderConfirmPaymentDialog
          order={{ ...confirming, payment: confirming.payment }}
          token={session.accessToken}
          onDone={async (ok) => {
            if (ok) {
              setConfirming(null);
              notifyPendingWorkChanged();
            }
            setReloadKey((k) => k + 1);
          }}
          onClose={() => setConfirming(null)}
        />
      )}
    </main>
  );
}
